
import api from "./api";

/* ---------------- GET LEAVES ---------------- */

export const getLeaves = async () => {
  const res = await api.get(`/salary/leave_applications`);

  const raw = res.data?.data || [];

  const leaves = raw.map((l: any) => ({
    _id: String(l.id),
    employeeId: l.employeeID,
    employeeName: l.name || "Employee",
    leaveType: l.leaveType || "personal",
    startDate: l.fromDate,
    endDate: l.toDate,
    days: l.totalDays || 0,
    reason: l.reason,
    status: l.status?.toLowerCase(),
    appliedDate: l.applied_on || l.decision_on,
    approvedBy: l.decision_by || null,
    managerID: l.managerID || null,
    managerComments: "",
    workedOn: l.worked_on || null,
  }));

  return { leaves };
};

// -------------SHOW LEAVE EXCLUDE EMPLOYEE---------
export const getMyLeaves = async () => {
  const res = await api.get(`/salary/leave_applications_all`);

  const raw = res.data?.data || [];

  const Leaves = raw.map((l: any) => ({
    _id: String(l.id),
    employeeId: l.employeeID,
    employeeName: l.name || "Employee",
    leaveType: l.leaveType || "personal",
    startDate: l.fromDate,
    endDate: l.toDate,
    days: l.totalDays || 0,
    reason: l.reason,
    status: l.status?.toLowerCase(),
    appliedDate: l.applied_on || l.decision_on,
    approvedBy: l.decision_by || null,
    managerID: l.managerID || null,
    managerComments: "",
    workedOn: l.worked_on || null,
  }));

  return { Leaves };
};


/* ---------------- APPLY LEAVE ---------------- */
export const applyLeave = async (payload: {
  employeeID: string;
  managerID: string;
  name: string;
  leaveType: string;
  fromDate: string;
  toDate: string;
  totalDays: number;
  reason: string;
  status: string;
  // Set only when the leaveType is the compensatory earn marker.
  worked_on?: string;
  // Half-day leave (policy rule `leave.half_day`). Absent for a whole day.
  // The server re-derives totalDays from this rather than trusting the
  // client's 0.5, so the two cannot disagree.
  session?: "first_half" | "second_half" | string;
}) => {
  const res = await api.post("/salary/apply_leave", payload);
  return res.data;
};

/* ---------------- APPLY FOR COMPENSATORY LEAVE (EARN) ---------------- */
export const applyCompensatoryEarn = async (payload: {
  employeeID: string | null;
  managerID: string | null;
  epmtID: string | null;
  name: string;
  fromDate: string; // YYYY-MM-DD
  toDate: string;   // YYYY-MM-DD
  days: number;
  reason?: string;
  workedOn?: string; // YYYY-MM-DD — optional context for backwards compat
}) => {
  const res = await api.post("/salary/apply_leave", {
    employeeID: payload.employeeID,
    epmtID: payload.epmtID,
    managerID: payload.managerID || "",
    name: payload.name,
    leaveType: "compensatory_earn",
    fromDate: payload.fromDate,
    toDate: payload.toDate,
    totalDays: payload.days,
    reason: payload.reason || "",
    status: "PENDING",
    appliedOn: new Date().toISOString(),
    worked_on: payload.workedOn || undefined,
  });
  return res.data;
};


/* ---------------- APPLY FOR MATERNITY / PATERNITY LEAVE (EARN) ----- */

async function applyParentalEarn(
  marker: "maternity_earn" | "paternity_earn",
  payload: {
    employeeID: string | null;
    managerID: string | null;
    epmtID: string | null;
    name: string;
    fromDate: string; // YYYY-MM-DD
    toDate: string;   // YYYY-MM-DD
    days: number;
    reason?: string;
  },
) {
  const res = await api.post("/salary/apply_leave", {
    employeeID: payload.employeeID,
    epmtID: payload.epmtID,
    managerID: payload.managerID || "",
    name: payload.name,
    leaveType: marker,
    fromDate: payload.fromDate,
    toDate: payload.toDate,
    totalDays: payload.days,
    reason: payload.reason || "",
    status: "PENDING",
    appliedOn: new Date().toISOString(),
  });
  return res.data;
}

export const applyMaternityEarn = (payload: {
  employeeID: string | null;
  managerID: string | null;
  epmtID: string | null;
  name: string;
  fromDate: string;
  toDate: string;
  days: number;
  reason?: string;
}) => applyParentalEarn("maternity_earn", payload);

export const applyPaternityEarn = (payload: {
  employeeID: string | null;
  managerID: string | null;
  epmtID: string | null;
  name: string;
  fromDate: string;
  toDate: string;
  days: number;
  reason?: string;
}) => applyParentalEarn("paternity_earn", payload);


/* ---------------- APPROVE / REJECT ---------------- */
export const updateLeaveStatus = async (
  leaveId: string,
  status: "approved" | "rejected",
  comments?: string
) => {
  const backendStatus = status.toUpperCase(); 

  const res = await api.put(
    `/salary/approve_leave/${leaveId}?status=${backendStatus}`
  );

  return res.data;
};

/* ---------------- LEAVE BALANCE (EMPLOYEE ONLY) ---------------- */
// export const getLeaveBalance = async () => {
//   try {
//     const res = await api.get(
//       `/salary/get_details_for_leave_application`
//     );

//     return {
//       balance: res.data?.data || {},
//     };
//   } catch (err: any) {
//     return { balance: {} };
//   }
// };

export const getLeaveBalance = async () => {
  const res = await api.get(
    `/salary/get_details_for_leave_application`
  );

  return {
    balance: res.data?.data?.balance || {},
    managerID: res.data?.data?.managerID || null,
    employeeID: res.data?.data?.employeeID || null,
    epmtID: res.data?.data?.id || null,
    gender: (res.data?.data?.gender || "").toString().toLowerCase() || null,
    // Which half-day sessions this person may ask for. The server resolves it
    // (the rule is per-branch, date-windowed and can be predicated on the
    // person), so the form only renders what comes back. An empty list means
    // the control is not offered at all.
    halfDay: {
      enabled: Boolean(res.data?.data?.half_day?.enabled),
      sessions: (res.data?.data?.half_day?.sessions || []) as string[],
    },
  };
};


/* ---------------- CANCEL (OPTIONAL) ---------------- */
export const cancelLeave = async (leaveId: string) => {
  const res = await api.put(`/salary/cancel_leave/${leaveId}`);
  return res.data;
};

/* ---------------- NOTIFICATION BELL FEED ---------------- */
// Leave approve/reject/apply notifications ride the shared notification bell
// via a namespaced task_id "leave:<id>" (they have no real Task). The bell
// hydrates the unread feed from here and marks rows read on dismissal.
export interface LeaveNotification {
  id: string;
  kind: string; // APPLIED | APPROVED | REJECTED
  source: "leave";
  leave_id: string | null;
  task_id: string; // "leave:<id>"
  title: string;
  body: string | null;
  is_read: boolean;
  created_at: string;
}

export const getLeaveNotifications = (limit = 50) =>
  api.get<{ total: number; items: LeaveNotification[] }>(
    `/salary/leave_notifications/unread?limit=${limit}`
  );

export const markLeaveNotificationRead = (id: string) =>
  api.post(`/salary/leave_notifications/${id}/read`);
